/**
 * Superadmin control-plane landing (ADR-0041 §10 — platform usage/health surface).
 * A glanceable board: tenant counters + an "attention" list (migration drift /
 * incomplete schema / suspended) + AGGREGATE consumption across tenants and a Top-10
 * of the heaviest tenants. Composed from the existing diagnostics endpoints + the
 * `/dashboard/usage` aggregate. Each row deep-links to that tenant's detail page.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Building2,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  Database,
  Loader2,
  Pause,
  PlugZap,
  ServerOff,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { type LicenseAttentionKind, licenseAttention } from "@/lib/licenseReport";
import { platformApi } from "@/lib/platform";

/** The axes surfaced as aggregate cards + Top-10 columns (order = display order). */
const USAGE_AXES = [
  "max_executions_month",
  "max_app_rows",
  "max_storage_gb",
  "max_users",
  "max_apps",
  "max_workflows",
  "max_etl_pipelines",
] as const;

/** Format an aggregate usage value — storage in GB to 2 decimals (trailing zeros
 * dropped, so 0.0114 → "0.01" and 5 → "5"); everything else as a plain count. */
function fmtUsage(axis: string, value: number): string {
  if (axis === "max_storage_gb") return Number(value.toFixed(2)).toLocaleString();
  return value.toLocaleString();
}

interface Attention {
  tenantId: string;
  name: string;
  schema: string;
  kind:
    | "migration"
    | "schema"
    | "suspended"
    | "past_due"
    | "cancel_scheduled"
    | LicenseAttentionKind;
  detail: string;
  /** Where the row navigates. Defaults to the tenant detail; an ON-PREM row has no tenant
   *  to open, so it points at the Licenze console instead. */
  to?: string;
}

/** Subscription states surfaced as count cards (order = display order). */
const BILLING_STATES = [
  { key: "active", label: "Paganti", tone: "success" as const },
  { key: "past_due", label: "In ritardo", tone: "danger" as const },
  { key: "cancel_scheduled", label: "In cancellazione", tone: "warning" as const },
  { key: "canceled", label: "Annullati", tone: "danger" as const },
  { key: "none", label: "Senza piano / comp", tone: "neutral" as const },
] as const;

/** Days until an ISO date (whole days, can be negative). */
function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
}

export function PlatformDashboardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sortAxis, setSortAxis] = React.useState<string>("max_executions_month");
  const statsQ = useQuery({ queryKey: ["platform", "tenants", "stats"], queryFn: () => platformApi.stats() });
  const tenantsQ = useQuery({ queryKey: ["platform", "tenants"], queryFn: () => platformApi.listTenants() });
  const overviewQ = useQuery({
    queryKey: ["platform", "tenants", "overview"],
    queryFn: () => platformApi.tenantsOverview(),
  });
  const usageQ = useQuery({
    queryKey: ["platform", "dashboard", "usage"],
    queryFn: () => platformApi.dashboardUsage(),
  });
  const billingQ = useQuery({
    queryKey: ["platform", "tenants", "billing"],
    queryFn: () => platformApi.tenantsBillingOverview(),
  });
  // On-prem installs are NOT tenants, so they're invisible to every query above — without
  // this the board silently ignores the whole on-prem fleet (ADR-0074 L1).
  const licensesQ = useQuery({
    queryKey: ["platform", "licenses"],
    queryFn: () => platformApi.listLicenses(),
  });
  const axisLabel = (key: string) => t(`billing.axis.${key}`, key);

  const tenants = tenantsQ.data ?? [];
  const overview = overviewQ.data ?? [];
  const billing = billingQ.data ?? [];
  const licenses = licensesQ.data ?? [];
  const nameById = new Map(tenants.map((t) => [t.id, t.name || t.slug]));

  // Subscription counts by derived state + the renewals due in the next 30 days.
  const billingCounts = billing.reduce<Record<string, number>>((acc, b) => {
    acc[b.state] = (acc[b.state] ?? 0) + 1;
    return acc;
  }, {});
  const renewals30 = billing
    .filter((b) => {
      if (b.state !== "active" && b.state !== "cancel_scheduled") return false;
      const d = daysUntil(b.current_period_end);
      return d !== null && d >= 0 && d <= 30;
    })
    .sort((a, b) => (daysUntil(a.current_period_end) ?? 0) - (daysUntil(b.current_period_end) ?? 0));

  const suspended = tenants.filter((t) => t.status === "suspended").length;
  const behind = overview.filter((h) => h.migration?.behind).length;

  // Build the attention list — one row per problem, most actionable first.
  // A remote tenant (hosted on its cell — split Control) has no local verdict:
  // never a fake "da migrare" row for it.
  const attention: Attention[] = [];
  for (const h of overview) {
    if (h.hosted_here === false || !h.migration || !h.schema) continue;
    const name = nameById.get(h.tenant_id) ?? h.slug;
    if (h.migration.behind) {
      attention.push({
        tenantId: h.tenant_id,
        name,
        schema: h.schema_name,
        kind: "migration",
        detail: `Migrazione indietro (${h.migration.current ?? "—"} → ${h.migration.head ?? "—"})`,
      });
    } else if (!h.schema.complete) {
      attention.push({
        tenantId: h.tenant_id,
        name,
        schema: h.schema_name,
        kind: "schema",
        detail: `${h.schema.missing_tables.length} tabelle mancanti`,
      });
    }
  }
  for (const t of tenants) {
    if (t.status === "suspended") {
      attention.push({
        tenantId: t.id,
        name: t.name || t.slug,
        schema: t.schema_name,
        kind: "suspended",
        detail: "Tenant sospeso",
      });
    }
  }
  // Billing problems — a payment in arrears or a scheduled churn the CC should chase.
  for (const b of billing) {
    const name = nameById.get(b.tenant_id) ?? b.slug;
    if (b.state === "past_due") {
      attention.push({
        tenantId: b.tenant_id,
        name,
        schema: b.slug,
        kind: "past_due",
        detail: b.billing_suspended ? "Pagamento in ritardo · sospeso" : "Pagamento in ritardo",
      });
    } else if (b.state === "cancel_scheduled") {
      const d = daysUntil(b.current_period_end);
      attention.push({
        tenantId: b.tenant_id,
        name,
        schema: b.slug,
        kind: "cancel_scheduled",
        detail: d !== null && d >= 0 ? `In cancellazione · ${d}g alla fine` : "In cancellazione",
      });
    }
  }
  // On-prem installs (ADR-0074 L1) — offline vs silent are DIFFERENT problems, so
  // `licenseAttention` keeps them apart rather than lumping them into one "stale" row.
  for (const lic of licenses) {
    const problem = licenseAttention(lic);
    if (!problem) continue;
    attention.push({
      tenantId: lic.id,
      name: lic.organization,
      schema: `${lic.activation_key_prefix}…`,
      kind: problem.kind,
      detail: problem.detail,
      to: "licenses",
    });
  }

  const loading = statsQ.isLoading || tenantsQ.isLoading || overviewQ.isLoading;

  const usageRows = usageQ.data?.tenants ?? [];
  const totals = usageQ.data?.totals ?? {};
  const top10 = [...usageRows]
    .sort((a, b) => (b.usage[sortAxis] ?? 0) - (a.usage[sortAxis] ?? 0))
    .slice(0, 10);
  const maxSort = top10.length ? Math.max(...top10.map((r) => r.usage[sortAxis] ?? 0), 1) : 1;

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        description="Stato della piattaforma: tenant, salute degli schemi e cose che richiedono attenzione."
      />
      <PageContent className="space-y-6">
        {/* Counters */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="Tenant totali" value={statsQ.data?.total} icon={Building2} />
          <StatCard label="Attivi" value={statsQ.data?.active} icon={CheckCircle2} tone="success" />
          <StatCard label="Sospesi" value={tenantsQ.isLoading ? undefined : suspended} icon={Pause} tone={suspended ? "warning" : "neutral"} />
          <StatCard label="Da migrare" value={overviewQ.isLoading ? undefined : behind} icon={Wrench} tone={behind ? "danger" : "neutral"} />
        </div>

        {/* Attention list */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <TriangleAlert className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="text-sm font-semibold text-foreground">Richiede attenzione</h2>
            {attention.length > 0 && <Badge tone="warning">{attention.length}</Badge>}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
            </div>
          ) : attention.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-4 py-6 text-sm text-foreground">
              <CheckCircle2 className="h-4 w-4 text-success" strokeWidth={1.5} />
              Tutto in ordine — nessun tenant né installazione richiede attenzione.
            </div>
          ) : (
            <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60">
              {attention.map((a, i) => (
                <li key={`${a.tenantId}-${a.kind}-${i}`}>
                  <button
                    type="button"
                    onClick={() => navigate(a.to ?? `tenants/${a.tenantId}`)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                  >
                    <AttentionIcon kind={a.kind} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-foreground">{a.name}</div>
                      <div className="truncate font-mono text-xs text-muted-foreground">{a.schema}</div>
                    </div>
                    <span className="shrink-0 text-xs text-muted-foreground">{a.detail}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Subscriptions — counts by state + the renewals due soon (ADR-0041 §13) */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="text-sm font-semibold text-foreground">Abbonamenti</h2>
          </div>
          {billingQ.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {BILLING_STATES.map((s) => (
                <BillingStat key={s.key} label={s.label} value={billingCounts[s.key] ?? 0} tone={s.tone} />
              ))}
              <BillingStat label="Rinnovi 30 giorni" value={renewals30.length} tone="neutral" />
            </div>
          )}
          {renewals30.length > 0 && (
            <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60">
              {renewals30.slice(0, 8).map((b) => {
                const d = daysUntil(b.current_period_end) ?? 0;
                return (
                  <li key={b.tenant_id}>
                    <button
                      type="button"
                      onClick={() => navigate(`tenants/${b.tenant_id}`)}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/50"
                    >
                      <CalendarClock className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {nameById.get(b.tenant_id) ?? b.slug}
                          {b.state === "cancel_scheduled" && (
                            <span className="ml-2 text-xs font-normal text-warning">in cancellazione</span>
                          )}
                        </div>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {new Date(b.current_period_end as string).toLocaleDateString()} · {d}g
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Aggregate consumption across tenants (ADR-0041 §10) */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="text-sm font-semibold text-foreground">Consumi aggregati</h2>
          </div>
          {usageQ.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {USAGE_AXES.map((axis) => (
                <UsageStat key={axis} axis={axis} label={axisLabel(axis)} value={totals[axis]} />
              ))}
            </div>
          )}
        </section>

        {/* Top-10 heaviest tenants */}
        <section className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
              <h2 className="text-sm font-semibold text-foreground">Top utilizzatori</h2>
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Ordina per
              <select
                aria-label="Metrica di ordinamento"
                value={sortAxis}
                onChange={(e) => setSortAxis(e.target.value)}
                className="h-8 rounded-md border border-border bg-transparent px-2 text-xs text-foreground"
              >
                {USAGE_AXES.map((axis) => (
                  <option key={axis} value={axis}>
                    {axisLabel(axis)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {usageQ.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
            </div>
          ) : top10.length === 0 ? (
            <div className="rounded-lg border border-border/60 px-4 py-6 text-sm text-muted-foreground">
              Nessun consumo registrato.
            </div>
          ) : (
            <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border/60">
              {top10.map((row, i) => {
                const v = row.usage[sortAxis] ?? 0;
                return (
                  <li key={row.tenant_id}>
                    <button
                      type="button"
                      onClick={() => navigate(`tenants/${row.tenant_id}`)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                    >
                      <span className="w-5 shrink-0 text-center text-xs tabular-nums text-muted-foreground">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-foreground">
                          {row.name || row.slug}
                        </div>
                        <div className="mt-1 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-elevated">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${Math.max(3, Math.round((v / maxSort) * 100))}%` }}
                          />
                        </div>
                      </div>
                      <span className="shrink-0 text-sm tabular-nums font-medium text-foreground">
                        {fmtUsage(sortAxis, v)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </PageContent>
    </PageContainer>
  );
}

function AttentionIcon({ kind }: { kind: Attention["kind"] }) {
  if (kind === "migration") return <Wrench className="h-4 w-4 shrink-0 text-danger" strokeWidth={1.5} />;
  if (kind === "suspended") return <Pause className="h-4 w-4 shrink-0 text-warning" strokeWidth={1.5} />;
  if (kind === "past_due") return <CreditCard className="h-4 w-4 shrink-0 text-danger" strokeWidth={1.5} />;
  if (kind === "cancel_scheduled") return <CalendarClock className="h-4 w-4 shrink-0 text-warning" strokeWidth={1.5} />;
  // An install we can't reach is an outage; one that merely stops reporting is an upgrade
  // to chase — different icons so the row is legible without reading the detail.
  if (kind === "license_offline") return <PlugZap className="h-4 w-4 shrink-0 text-danger" strokeWidth={1.5} />;
  if (kind === "license_silent") return <ServerOff className="h-4 w-4 shrink-0 text-warning" strokeWidth={1.5} />;
  return <TriangleAlert className="h-4 w-4 shrink-0 text-warning" strokeWidth={1.5} />;
}

/** A subscription-state count card — value tinted by tone (danger/warning pop). */
function BillingStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "success" | "danger" | "warning" | "neutral";
}) {
  const toneClass = {
    success: "text-success",
    danger: value > 0 ? "text-danger" : "text-foreground",
    warning: value > 0 ? "text-warning" : "text-foreground",
    neutral: "text-foreground",
  }[tone];
  return (
    <div className="rounded-xl border border-border/60 bg-surface/40 p-3">
      <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground" title={label}>
        {label}
      </div>
      <div className={`mt-1 text-xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

/** A compact aggregate-usage metric (label over summed value). */
function UsageStat({ axis, label, value }: { axis: string; label: string; value: number | undefined }) {
  return (
    <div className="rounded-xl border border-border/60 bg-surface/40 p-3">
      <div className="truncate text-[11px] uppercase tracking-wide text-muted-foreground" title={label}>
        {label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">
        {value === undefined ? "—" : fmtUsage(axis, value)}
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone = "neutral",
}: {
  label: string;
  value: number | undefined;
  icon: typeof Building2;
  tone?: "neutral" | "success" | "warning" | "danger";
}) {
  const toneClass = {
    neutral: "text-muted-foreground",
    success: "text-success",
    warning: "text-warning",
    danger: "text-danger",
  }[tone];
  return (
    <div className="rounded-xl border border-border/60 bg-surface/40 p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${toneClass}`} strokeWidth={1.5} />
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums text-foreground">{value ?? "—"}</div>
    </div>
  );
}
